import { createRef, memo, useState } from 'react';

import { FormError } from '@components/form-error';
import { FieldDescription, FormLabel } from '@components/form-label';
import { Stack } from '@components/stack';
import { randomId } from '@utils/strings';

import { FileAcceptText } from './file-accept-text';
import { FileInput } from './file-input';
import { FileInputContent } from './file-input-content';
import { useFileTranslations } from './locale';
import { DefaultFile, FileFieldBaseProps } from './types';
import { UploadedFile } from './uploaded-file';

export type FileFieldMultiProps = FileFieldBaseProps & {
  /**
   * Previously uploaded files
   */
  defaultValue?: Array<DefaultFile>;
  /**
   * Callback when user removes one of the supplied defaultFiles
   */
  onRemove?(file: DefaultFile): void;
  /**
   * Callback when the list of new files is updated
   */
  onChange(files: Array<File | DefaultFile>): void;
  description?: string;
};

const createFileEntry = () => ({
  id: randomId(),
  ref: createRef<HTMLInputElement>(),
  files: [] as Array<{ id: string; file: File }>,
});

type FileEntryType = ReturnType<typeof createFileEntry>;
type FileEntryFileType = FileEntryType['files'][number];

export const FileFieldMulti = memo<FileFieldMultiProps>(
  ({
    label,
    accept,
    error,
    onRemove,
    onChange,
    isDisabled,
    placeholder: userPlaceholder,
    defaultValue,
    description,
  }) => {
    const [defaultFiles, setDefaultFiles] = useState(defaultValue ?? []);
    const [fileEntries, setFileEntries] = useState([createFileEntry()]);
    const hasAnyFile =
      defaultFiles.length > 0 || fileEntries.some((fileEntry) => fileEntry.files.length > 0);
    const { t } = useFileTranslations();
    const placeholder = userPlaceholder ?? t('placeholder');

    const getFlatEntries = (entries: Array<FileEntryType>) => {
      return entries.map((entry) => entry.files.map((entryFile) => entryFile.file)).flat();
    };

    const updateEntries = (entries: Array<FileEntryType>) => {
      setFileEntries(entries);

      // Our local state is more complex so we can more easily add/remove files by IDs
      // but when we call onChange the files should be a flat list.
      onChange([...defaultFiles, ...getFlatEntries(entries)]);
    };

    const handleRemoveDefaultFile = (defaultFile: DefaultFile) => {
      onRemove?.(defaultFile);
      const newFiles = defaultFiles.filter((file) => file.id !== defaultFile.id);
      setDefaultFiles(newFiles);
      onChange([...newFiles, ...getFlatEntries(fileEntries)]);
    };

    return (
      <>
        <Stack space={1}>
          <FormLabel htmlFor={fileEntries[0].id} text={label} />
          <FieldDescription text={description} />

          {defaultFiles.map((defaultFile) => (
            <UploadedFile
              isDisabled={isDisabled}
              key={defaultFile.id}
              name={defaultFile.name}
              path={defaultFile.src}
              onRemove={() => handleRemoveDefaultFile(defaultFile)}
            />
          ))}

          <Stack space={2}>
            {fileEntries.map((fileEntry) => (
              <FileEntry
                isDisabled={isDisabled}
                key={fileEntry.id}
                placeholder={placeholder}
                accept={accept}
                fileEntry={fileEntry}
                allEntries={fileEntries}
                updateEntries={updateEntries}
                // An entry only shows the error on its empty drop area, so marking
                // it while files are attached puts the red border on the untouched
                // "add another" slot rather than on the file that caused the error.
                error={hasAnyFile ? undefined : error}
              />
            ))}
          </Stack>

          <FileAcceptText accept={accept} />
          <FormError text={error} isAnnounced />
        </Stack>
      </>
    );
  },
);

type FileEntryProps = {
  fileEntry: FileEntryType;
  allEntries: Array<FileEntryType>;
  updateEntries(newEntries: Array<FileEntryType>): void;
} & Pick<FileFieldMultiProps, 'accept' | 'placeholder' | 'error' | 'isDisabled'>;

const FileEntry = ({
  fileEntry,
  placeholder,
  accept,
  allEntries,
  updateEntries,
  error,
  isDisabled,
}: FileEntryProps) => {
  const handleInputChange = (files: Array<File>) => {
    const newEntryFiles = [
      ...fileEntry.files,
      ...files.map((f) => ({
        file: f,
        id: randomId(),
      })),
    ];

    const newEntries = allEntries.map((entry) => {
      if (entry.id === fileEntry.id) return { ...entry, files: newEntryFiles };
      return entry;
    });

    updateEntries(newEntries.concat(createFileEntry()));
  };

  const handleRemoveEntry = (entryFile: FileEntryFileType) => {
    const newFiles = fileEntry.files.filter((ef) => ef.id !== entryFile.id);

    const newEntries =
      newFiles.length > 0
        ? allEntries.map((entry) => {
            if (entry.id === fileEntry.id) return { ...entry, files: newFiles };
            return entry;
          })
        : allEntries.filter((entry) => entry.id !== fileEntry.id);

    updateEntries(newEntries);
  };

  return (
    <div>
      <FileInput
        isDisabled={isDisabled}
        isMulti
        id={fileEntry.id}
        ref={fileEntry.ref}
        accept={accept}
        onChange={handleInputChange}
      />

      <Stack space={2}>
        {fileEntry.files.map((entryFile) => (
          <UploadedFile
            isDisabled={isDisabled}
            key={entryFile.id}
            name={entryFile.file.name}
            onRemove={() => handleRemoveEntry(entryFile)}
          />
        ))}
      </Stack>

      {fileEntry.files.length === 0 && (
        <FileInputContent
          isDisabled={isDisabled}
          error={error}
          onClick={() => fileEntry.ref?.current?.click()}
          placeholder={placeholder}
        />
      )}
    </div>
  );
};
